import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, openPdf, printPdf } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, KV, StatusBadge, EmptyState } from "@/components/common/UI";
import { CustomInput } from "./PatientsPage";
import { fmtDate, fmtDateTime, groupBySection } from "@/lib/format";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Plus, Bed, Receipt, Printer } from "@phosphor-icons/react";
import MedicineRxList from "@/components/common/MedicineRxList";

export default function PatientDetailPage() {
  const { mrn } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const opdHidden = user?.hospital?.opd_module_enabled === false;
  const [patient, setPatient] = useState(null);
  const [opd, setOpd] = useState([]);
  const [admissions, setAdmissions] = useState([]);
  const [opdOpen, setOpdOpen] = useState(false);
  const [ipdOpen, setIpdOpen] = useState(false);
  const [opdCf, setOpdCf] = useState([]);
  const [ipdCf, setIpdCf] = useState([]);
  const [patientCf, setPatientCf] = useState([]);

  const load = useCallback(() => {
    api.get(`/patients/${mrn}`).then((r) => setPatient(r.data));
    api.get(`/patients/${mrn}/opd`).then((r) => setOpd(r.data || []));
    api.get(`/admissions`).then((r) => setAdmissions((r.data || []).filter((a) => a.mrn === mrn)));
  }, [mrn]);

  useEffect(() => {
    load();
    api.get("/custom-fields", { params: { entity: "patient" } }).then((r) => setPatientCf(r.data || []));
    api.get("/custom-fields", { params: { entity: "opd" } }).then((r) => setOpdCf(r.data || []));
    api.get("/custom-fields", { params: { entity: "ipd" } }).then((r) => setIpdCf(r.data || []));
  }, [load]);

  if (!patient) return <div className="text-sm text-[var(--hms-muted)]">Loading patient…</div>;

  return (
    <div data-testid="patient-detail-page">
      <Link to="/patients" className="inline-flex items-center gap-1 text-xs text-[var(--hms-text-2)] hover:text-[var(--hms-accent)] mb-3">
        <ArrowLeft size={12} weight="bold" /> Back to patients
      </Link>
      <PageHeader
        eyebrow={`MRN ${patient.mrn}`}
        title={patient.name}
        subtitle={`${patient.age || "—"} / ${patient.gender || "—"} · ${patient.phone || "no phone"} · ${patient.blood_group || "—"} blood`}
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge status={patient.status} />
            {!opdHidden && (
              <Dialog open={opdOpen} onOpenChange={setOpdOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="record-opd-btn" className="rounded-sm border-[var(--hms-border-2)]">
                    <Plus size={14} weight="bold" className="mr-1.5" /> Record OPD visit
                  </Button>
                </DialogTrigger>
                <OPDDialog mrn={mrn} cf={opdCf} onDone={() => { setOpdOpen(false); load(); }} />
              </Dialog>
            )}
            <Dialog open={ipdOpen} onOpenChange={setIpdOpen}>
              <DialogTrigger asChild>
                <Button data-testid="admit-ipd-btn" className="rounded-sm bg-[var(--hms-accent)] hover:bg-[var(--hms-accent)]/90">
                  <Bed size={14} weight="bold" className="mr-1.5" /> Admit to IPD
                </Button>
              </DialogTrigger>
              <IPDDialog mrn={mrn} cf={ipdCf} onAdmitted={(adm) => { setIpdOpen(false); nav(`/admissions/${adm.id}`); }} />
            </Dialog>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
        <div className="hms-card p-4 lg:col-span-1">
          <div className="hms-section-title mb-3">Profile</div>
          <div className="space-y-3">
            <KV label="MRN" value={<span className="font-mono">{patient.mrn}</span>} />
            <KV label="Phone" value={patient.phone} />
            <KV label="DOB" value={patient.dob} />
            <KV label="Address" value={patient.address} />
            <KV label="Emergency" value={patient.emergency_contact} />
            <KV label="Allergies" value={patient.allergies} />
            {Object.keys(patient.custom_fields || {}).length > 0 && (
              <CustomFieldsView fields={patientCf} values={patient.custom_fields} />
            )}
          </div>
        </div>

        <div className="lg:col-span-3">
          <Tabs defaultValue="opd" className="w-full">
            <TabsList className="bg-[var(--hms-paper)] rounded-sm border border-[var(--hms-border-2)]">
              <TabsTrigger value="opd" data-testid="tab-opd" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-none">OPD ({opd.length})</TabsTrigger>
              <TabsTrigger value="ipd" data-testid="tab-ipd" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-none">IPD ({admissions.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="opd" className="mt-3">
              {opd.length === 0 ? <EmptyState title="No OPD visits" hint="Record one from the action above." /> : (
                <div className="hms-card overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-[var(--hms-paper)] text-[10px] uppercase tracking-wider font-bold text-[var(--hms-text-2)] border-b border-[var(--hms-border-2)]">
                      <tr>
                        <th className="text-left px-4 py-2.5">Receipt</th>
                        <th className="text-left px-4 py-2.5">Date</th>
                        <th className="text-left px-4 py-2.5">Doctor</th>
                        <th className="text-left px-4 py-2.5">Complaints</th>
                        <th className="text-left px-4 py-2.5">Diagnosis</th>
                        <th className="text-right px-4 py-2.5">Fee</th>
                        <th className="text-right px-4 py-2.5">Receipt</th>
                        <th className="text-right px-4 py-2.5">Rx</th>
                      </tr>
                    </thead>
                    <tbody>
                      {opd.map((v) => (
                        <tr key={v.id} className="border-b border-[var(--hms-border)]">
                          <td className="px-4 py-2 font-mono text-xs font-semibold text-[var(--hms-accent)]">{v.receipt_no || "—"}</td>
                          <td className="px-4 py-2 hms-tabular">{fmtDateTime(v.visit_date)}</td>
                          <td className="px-4 py-2 font-medium">{v.doctor}</td>
                          <td className="px-4 py-2 text-[var(--hms-text-2)]">{v.complaints}</td>
                          <td className="px-4 py-2">{v.diagnosis}</td>
                          <td className="px-4 py-2 text-right hms-tabular">
                            ₹{Number(v.fee || 0).toFixed(2)}
                            {v.payment_mode && <div className="text-[10px] text-[var(--hms-muted)] uppercase tracking-wider">{v.payment_mode}</div>}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <button
                              onClick={() => printPdf(`/pdf/opd-receipt/${v.id}`).catch((e) => toast.error(e.message || "Print failed"))}
                              data-testid={`opd-receipt-print-${v.id}`}
                              className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-[var(--hms-text-2)] hover:text-[var(--hms-accent)] hover:bg-[var(--hms-hover)] cursor-pointer transition-colors"
                              title="Print OPD receipt (A5)"
                              aria-label="Print OPD receipt"
                            >
                              <Receipt size={16} weight="bold" />
                            </button>
                          </td>
                          <td className="px-4 py-2 text-right">
                            <button
                              onClick={() => printPdf(`/pdf/opd-rx/${v.id}`).catch((e) => toast.error(e.message || "Print failed"))}
                              data-testid={`opd-rx-print-${v.id}`}
                              className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-[var(--hms-text-2)] hover:text-[var(--hms-accent)] hover:bg-[var(--hms-hover)] cursor-pointer transition-colors"
                              title="Print prescription (A5)"
                              aria-label="Print prescription"
                            >
                              <Printer size={16} weight="bold" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="ipd" className="mt-3">
              {admissions.length === 0 ? <EmptyState title="No IPD admissions" hint="Admit this patient to start an IPD record." /> : (
                <div className="hms-card overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-[var(--hms-paper)] text-[10px] uppercase tracking-wider font-bold text-[var(--hms-text-2)] border-b border-[var(--hms-border-2)]">
                      <tr>
                        <th className="text-left px-4 py-2.5">IPD No.</th>
                        <th className="text-left px-4 py-2.5">Admit Date</th>
                        <th className="text-left px-4 py-2.5">Ward/Bed</th>
                        <th className="text-left px-4 py-2.5">Doctor</th>
                        <th className="text-left px-4 py-2.5">Status</th>
                        <th className="text-right px-4 py-2.5"></th>
                        <th className="text-right px-4 py-2.5">Print</th>
                      </tr>
                    </thead>
                    <tbody>
                      {admissions.map((a) => (
                        <tr key={a.id} className="border-b border-[var(--hms-border)]">
                          <td className="px-4 py-2 font-mono text-xs">{a.admission_no}</td>
                          <td className="px-4 py-2 hms-tabular">{fmtDate(a.admit_date)}</td>
                          <td className="px-4 py-2">{a.ward} / {a.bed}</td>
                          <td className="px-4 py-2">{a.doctor}</td>
                          <td className="px-4 py-2"><StatusBadge status={a.status} /></td>
                          <td className="px-4 py-2 text-right">
                            <Link to={`/admissions/${a.id}`} className="text-xs font-bold text-[var(--hms-accent)] hover:underline">Open</Link>
                          </td>
                          <td className="px-4 py-2 text-right">
                            <button
                              onClick={() => printPdf(a.status === "discharged" ? `/pdf/discharge/${a.id}` : `/pdf/admit-card/${a.id}`).catch((e) => toast.error(e.message || "Print failed"))}
                              data-testid={`patient-${a.status === "discharged" ? "discharge" : "admit"}-print-${a.admission_no}`}
                              title={a.status === "discharged" ? "Print discharge card" : "Print admit card"}
                              aria-label={a.status === "discharged" ? "Print discharge card" : "Print admit card"}
                              className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-[var(--hms-text-2)] hover:text-[var(--hms-accent)] hover:bg-[var(--hms-hover)] cursor-pointer transition-colors"
                            >
                              <Printer size={16} weight="bold" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

function OPDDialog({ mrn, cf, onDone }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ doctor: "", department: "", complaints: "", diagnosis: "", prescription: "", fee: 0, payment_mode: "Cash", payment_reference: "", medicines: [], custom_fields: {} });
  const [doctors, setDoctors] = useState([]);
  const [departments, setDepartments] = useState([]);
  useEffect(() => {
    api.get("/doctors").then((r) => setDoctors(r.data || []));
    api.get("/departments").then((r) => setDepartments(r.data || []));
  }, []);

  const onPickDoctor = (id) => {
    const d = doctors.find((x) => x.id === id);
    if (d) setForm((f) => ({ ...f, doctor: d.name, department: d.department || f.department, fee: d.consultation_fee || f.fee }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const medicines = (form.medicines || []).filter((m) => (m.name || "").trim());
      await api.post(`/patients/${mrn}/opd`, { ...form, fee: Number(form.fee || 0), medicines });
      toast.success("OPD visit recorded");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <DialogContent className="rounded-sm max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-heading">New OPD visit</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="grid grid-cols-2 gap-3" data-testid="opd-form">
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Doctor *</Label>
          {doctors.length > 0 ? (
            <Select onValueChange={onPickDoctor}>
              <SelectTrigger data-testid="opd-doctor-select" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"><SelectValue placeholder={form.doctor || "Choose doctor"} /></SelectTrigger>
              <SelectContent>
                {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}{d.specialization ? ` · ${d.specialization}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input value={form.doctor} onChange={(e) => setForm({ ...form, doctor: e.target.value })} required data-testid="opd-doctor" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" />
          )}
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Department</Label>
          {departments.length > 0 ? (
            <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
              <SelectTrigger data-testid="opd-dept-select" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="opd-dept" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" />
          )}
        </div>
        <Field label="Complaints" value={form.complaints} onChange={(v) => setForm({ ...form, complaints: v })} testid="opd-complaints" colSpan={2} />
        <Field label="Diagnosis" value={form.diagnosis} onChange={(v) => setForm({ ...form, diagnosis: v })} testid="opd-diag" colSpan={2} />
        <div className="col-span-2">
          <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Prescription (free-text notes)</Label>
          <Textarea value={form.prescription} onChange={(e) => setForm({ ...form, prescription: e.target.value })} className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" data-testid="opd-rx" />
        </div>
        <div className="col-span-2 border-t border-[var(--hms-border)] pt-3 mt-1">
          <MedicineRxList
            value={form.medicines}
            onChange={(meds) => setForm({ ...form, medicines: meds })}
            title="Medicines (Rx)"
            testidPrefix="opd-rx"
          />
        </div>
        <Field label="Fee" type="number" value={form.fee} onChange={(v) => setForm({ ...form, fee: v })} testid="opd-fee" />
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Payment mode</Label>
          <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
            <SelectTrigger data-testid="opd-pay-mode" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Cash">Cash</SelectItem>
              <SelectItem value="Card">Card</SelectItem>
              <SelectItem value="UPI">UPI</SelectItem>
              <SelectItem value="Bank">Bank Transfer</SelectItem>
              <SelectItem value="Cheque">Cheque</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Field label="Payment reference" value={form.payment_reference} onChange={(v) => setForm({ ...form, payment_reference: v })} testid="opd-pay-ref" colSpan={2} />
        {cf.length > 0 && (
          <div className="col-span-2 border-t border-[var(--hms-border)] pt-3 mt-1 space-y-3">
            <CustomFieldsForm fields={cf} values={form.custom_fields}
              onChange={(k, v) => setForm({ ...form, custom_fields: { ...form.custom_fields, [k]: v } })}
              cols={2} />
          </div>
        )}
        <DialogFooter className="col-span-2 mt-2">
          <Button type="submit" disabled={busy} data-testid="opd-submit" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">
            {busy ? "Saving…" : "Save visit"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function IPDDialog({ mrn, cf, onAdmitted }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ doctor: "", department: "", ward: "", bed: "", bed_id: "", diagnosis: "", admission_type: "Normal", custom_fields: {} });
  const [doctors, setDoctors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [wards, setWards] = useState([]);
  const [beds, setBeds] = useState([]);
  const [selectedWardId, setSelectedWardId] = useState("");

  useEffect(() => {
    api.get("/doctors").then((r) => setDoctors(r.data || []));
    api.get("/departments").then((r) => setDepartments(r.data || []));
    api.get("/wards").then((r) => setWards(r.data || []));
  }, []);
  useEffect(() => {
    if (!selectedWardId) { setBeds([]); return; }
    api.get("/beds", { params: { ward_id: selectedWardId, available_only: true } })
      .then((r) => setBeds(r.data || []));
  }, [selectedWardId]);

  const onPickDoctor = (id) => {
    const d = doctors.find((x) => x.id === id);
    if (d) setForm((f) => ({ ...f, doctor: d.name, department: d.department || f.department }));
  };
  const onPickWard = (id) => {
    setSelectedWardId(id);
    const w = wards.find((x) => x.id === id);
    setForm((f) => ({ ...f, ward: w ? w.name : "", bed: "", bed_id: "" }));
  };
  const onPickBed = (id) => {
    const b = beds.find((x) => x.id === id);
    setForm((f) => ({ ...f, bed_id: id, bed: b ? b.bed_no : "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/patients/${mrn}/admit`, form);
      toast.success(`Admitted: ${data.admission_no}`);
      onAdmitted(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <DialogContent className="rounded-sm max-w-2xl">
      <DialogHeader><DialogTitle className="font-heading">Admit to IPD</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="grid grid-cols-2 gap-3" data-testid="ipd-form">
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Consultant Doctor *</Label>
          {doctors.length > 0 ? (
            <Select onValueChange={onPickDoctor}>
              <SelectTrigger data-testid="ipd-doctor-select" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"><SelectValue placeholder={form.doctor || "Choose doctor"} /></SelectTrigger>
              <SelectContent>
                {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}{d.specialization ? ` · ${d.specialization}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input value={form.doctor} onChange={(e) => setForm({ ...form, doctor: e.target.value })} required
              data-testid="ipd-doctor" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" placeholder="Type doctor name (add masters for dropdown)" />
          )}
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Department</Label>
          {departments.length > 0 ? (
            <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
              <SelectTrigger data-testid="ipd-dept-select" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="ipd-dept" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" />
          )}
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Ward *</Label>
          {wards.length > 0 ? (
            <Select value={selectedWardId} onValueChange={onPickWard}>
              <SelectTrigger data-testid="ipd-ward-select" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"><SelectValue placeholder="Select ward" /></SelectTrigger>
              <SelectContent>
                {wards.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}{w.type ? ` · ${w.type}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input value={form.ward} onChange={(e) => setForm({ ...form, ward: e.target.value })} required data-testid="ipd-ward" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" placeholder="Type ward (add masters for dropdown)" />
          )}
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Bed No. *</Label>
          {wards.length > 0 ? (
            <Select value={form.bed_id} onValueChange={onPickBed} disabled={!selectedWardId}>
              <SelectTrigger data-testid="ipd-bed-select" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white">
                <SelectValue placeholder={selectedWardId ? (beds.length === 0 ? "No available beds" : "Select bed") : "Pick ward first"} />
              </SelectTrigger>
              <SelectContent>
                {beds.map((b) => <SelectItem key={b.id} value={b.id}>{b.bed_no}{b.daily_rate ? ` · ₹${Number(b.daily_rate).toFixed(0)}/day` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input value={form.bed} onChange={(e) => setForm({ ...form, bed: e.target.value })} required data-testid="ipd-bed" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" />
          )}
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Type</Label>
          <Select value={form.admission_type} onValueChange={(v) => setForm({ ...form, admission_type: v })}>
            <SelectTrigger data-testid="ipd-type" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Normal">Normal</SelectItem>
              <SelectItem value="Emergency">Emergency</SelectItem>
              <SelectItem value="Insurance">Insurance</SelectItem>
              <SelectItem value="Day-Care">Day-Care</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Field label="Provisional diagnosis" value={form.diagnosis} onChange={(v) => setForm({ ...form, diagnosis: v })} testid="ipd-diag" colSpan={2} />
        {cf.length > 0 && (
          <div className="col-span-2 border-t border-[var(--hms-border)] pt-3 mt-1 space-y-3">
            <CustomFieldsForm fields={cf} values={form.custom_fields}
              onChange={(k, v) => setForm({ ...form, custom_fields: { ...form.custom_fields, [k]: v } })}
              cols={2} />
          </div>
        )}
        <DialogFooter className="col-span-2 mt-2">
          <Button type="submit" disabled={busy} data-testid="ipd-submit" className="rounded-sm bg-[var(--hms-accent)] hover:bg-[var(--hms-accent)]/90">
            {busy ? "Admitting…" : "Admit & generate IPD No."}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function Field({ label, value, onChange, required, type, testid, colSpan }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">{label}{required && " *"}</Label>
      <Input type={type || "text"} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        data-testid={testid} className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" />
    </div>
  );
}


function CustomFieldsForm({ fields, values, onChange, cols = 3 }) {
  const groups = groupBySection(fields);
  const grid = cols === 2 ? "grid-cols-2" : "grid-cols-2 md:grid-cols-3";
  return (
    <>
      {Object.entries(groups).map(([section, list]) => (
        <div key={section}>
          <div className="hms-section-title mb-2">{section}</div>
          <div className={`grid ${grid} gap-3`}>
            {list.map((f) => (
              <CustomInput key={f.id} field={f} value={values?.[f.key]}
                onChange={(v) => onChange(f.key, v)} />
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

function CustomFieldsView({ fields, values }) {
  // Render values grouped by section using field labels (not raw keys)
  const present = (fields || []).filter((f) => values?.[f.key] !== undefined && values?.[f.key] !== "");
  if (present.length === 0) {
    // Fallback: show whatever keys exist
    return (
      <div className="border-t border-[var(--hms-border)] pt-3 space-y-1">
        <div className="hms-section-title mb-1 text-[10px]">Custom</div>
        {Object.entries(values).map(([k, v]) => <KV key={k} label={k} value={String(v)} />)}
      </div>
    );
  }
  const groups = groupBySection(present);
  return (
    <div className="border-t border-[var(--hms-border)] pt-3 space-y-3">
      {Object.entries(groups).map(([section, list]) => (
        <div key={section}>
          <div className="hms-section-title mb-1 text-[10px]">{section}</div>
          <div className="space-y-2">
            {list.map((f) => <KV key={f.id} label={f.label} value={String(values[f.key] ?? "")} />)}
          </div>
        </div>
      ))}
    </div>
  );
}
